/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import "./Issues.scss";

import React, { FunctionComponent, useEffect, useState } from "react";

import { AbstractWidgetProps, StagePanelLocation, StagePanelSection, UiItemsProvider, WidgetState } from "@itwin/appui-abstract";
import { Select, Input, ToggleSwitch, Button } from "@itwin/itwinui-react";
import { IModelApp } from "@itwin/core-frontend";
import { CssProperties } from "@itwin/appui-layout-react";
import { Point3d } from "@itwin/core-geometry";

import { cIssue, IssueData, IssueMarkerType } from "./Issues";
import { cIssueDlg } from "./IssuesDlg";
import { cIssuesTable } from "./IssuesTable";
import { cPlacementTool } from "./PlacementTool";
import { cView } from "./View";
import { cViewClip } from "./ViewClip";
import { cMessage } from "./Message";
import { cMarker } from "./Marker";

export class cIssuesUi {
  public static onStartPlaceMarkerTool = () => {
    if (cIssueDlg.isDialogOpen()) {
      cMessage.showInfo("Please close current dialog before creating a new Event");
      return;
    }

    cMessage.showStatusBarMessage("Select Event location", "icon-cursor-click");
    IModelApp.tools.run(cPlacementTool.toolId, cIssuesUi.newIssueLocation);
}

  /** This callback will be executed by the PlacementTool when it is time to create a new marker */
  public static newIssueLocation = (point: Point3d) => {
    cMessage.clearStatusBarMessage();
    cIssueDlg.newIssue(point);
  }

  public static goToIssue = (issue: IssueData) => {
    if (issue) {
      //      view3d.setupFromFrustum(issue.frustum!, viewChangeOpts);
      //      const frustum = new Frustum();
      //      frustum.initFromRange(issue.frustum!);
      //      view3d.setupFromFrustum(issue.frustum!, viewChangeOpts);
      //const directionVector = Point3d.fromJSON(cameraView.direction);

      // //      const eye = issue.location.plusXYZ(-1,1,0.5);
      // //      const targetPoint = eyePoint.plus(directionVector);
      //       view3d.lookAtUsingLensAngle(eye, target, upVector, Angle.createRadians(fov), 0.5, undefined, viewChangeOpts);
      //       viewport.turnCameraOff();
      //      cView.zoomToRange(issue.frustum!);

      if (issue.eyePoint && issue.targetPoint) {
        cView.setCameraView(issue.eyePoint, issue.targetPoint);
      } else {
        // setup arbitrary eye location close to and looking at issue location
        const targetPoint = issue.location;
        cView.setCameraView(targetPoint.plusXYZ(-1, 1, 0.5), targetPoint);
      }

      if (issue.clipRange && !issue.clipRange.isNull) {
        cViewClip.clipToRange(issue.clipRange);
      }
    }
  }

  public static applyFilter(filter: IssueData) {
    cIssue.showFilter(filter);
  }

  public static setMarkerImageType(type: IssueMarkerType) {
    cIssue.setMarkerImageType(type);
    cIssue.removeAllMarkers();
    cIssue.showAllMarkers();
  }
}

//==============================================================================
// IssesUiElements: issues related controls on main widget
//==============================================================================
export const IssuesUiElements: FunctionComponent = () => {
  const _filter = new IssueData();
  const [_showIssues, setShowIssues] = useState(false);
  const [_showLabel, setShowLabel] = useState(false);
  const [_selected, setSelected] = useState("");
  const [_showIcons, setShowIcons] = useState(true);
  const [_filterType, setFilterType] = useState("All");
  const [_filterPriority, setFilterPriority] = useState("All");
  const [_filterStatus, setFilterStatus] = useState("All");
  const [_filterDate, setFilterDate] = useState("");
  const [_filterAssigned, setFilterAssigned] = useState("All");
  const [_tag, setTag] = useState("");

  const btnActive = "#005596"; // dark dogerblue

  // useEffect(() => {
  //   cIssuesUi.setMarkerImageType(IssueMarkerType.Icon);
  //   setShowIcons(true);
  //   setTag("");
  // }, [_showIssues]);

  useEffect(() => {
    const searchHTML = document.getElementById("search") as HTMLInputElement;
    if (searchHTML) {
      searchHTML.value = _selected; //!!OA
    }
  }, [_selected]);

  useEffect(() => {
    const sinceHTML = document.getElementById("since") as HTMLInputElement;
    if (sinceHTML) {
      sinceHTML.value = _filterDate; //!!OA
    }
  }, [_filterDate]);

  useEffect(() => {
    const assignHTML = document.getElementById("assigned") as HTMLInputElement;
    if (assignHTML) {
      assignHTML.value = _filterAssigned; //!!OA
    }
  }, [_filterAssigned]);

  const onToggleShowIssues = (toggle: boolean) => {
    if (toggle) {
      cIssue.showAllMarkers();
      setShowIssues(true);
      cMarker.enableMarkerSet("Issues");

      cIssuesUi.setMarkerImageType(IssueMarkerType.Icon);
      setShowIcons(true);

      setTag("");
    } else {
      cIssue.removeAllMarkers();
      setShowIssues(false);
      cMarker.disableMarkerSet("Issues");

    }
  }

  const onChangeText = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = event.target.value;
    if (keyword === "") {
      // when x in search box is clicked
      cIssue.showAllMarkers();
      cMessage.clearStatusBarMessage();
    }
    setTag(keyword);
  }

  const onKeyDnSearch = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      cIssue.searchText(_tag);
    }
  }

  const onButtonSearch = async () => {
    cIssue.searchText(_tag);
  }

  const onShowTable = () => {
    cIssuesTable.openTable();
  }

  const onToggleShowIcon = () => {
    if (!_showIcons) {
      cIssuesUi.setMarkerImageType(IssueMarkerType.Icon);
      setShowIcons(true);
    } else {
      cIssuesUi.setMarkerImageType(IssueMarkerType.Graphics);
      setShowIcons(false);
    }
  }

  const onToggleShowLabel = () => {
    if (!_showLabel) {
      cIssue.showLabel(true);
      setShowLabel(true);
    } else {
      cIssue.showLabel(false);
      setShowLabel(false);
    }
  }

  const onGoToIssue = () => {
    const issue = cIssue.getIssueById(Number(_selected));
    cIssuesUi.goToIssue(issue);
  }

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const issueId = event.target.value;
    setSelected(issueId);
    // if (issueId && cIssue.getIssueById(issueId)) {
    //   issuesUi._selected = issueId;
    // }
  }

  const onSearchKeyUp = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      const issue = cIssue.getIssueById(Number(_selected));
      cIssuesUi.goToIssue(issue);
    }
  }

  const onChangeType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterType(event.target.value);
  }

  const onChangePriority = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterPriority(event.target.value);
  }

  const onChangeStatus = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterStatus(event.target.value);
  }

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterDate(event.target.value);
  }

  const onChangeAssignedTo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterAssigned(event.target.value);
  }

  const onApply = () => {
    _filter.type = _filterType.toLowerCase();
    _filter.priority = _filterPriority.toLowerCase();
    _filter.status = _filterStatus.toLowerCase();
    _filter.timeCreated = _filterDate.toLowerCase();
    _filter.assignedTo = _filterAssigned.toLowerCase();

    cIssuesUi.applyFilter(_filter);
  }

  const onClear = () => {
    setSelected("");
    setFilterType("All");
    setFilterPriority("All");
    setFilterStatus("All");
    setFilterDate("");
    setFilterAssigned("All");

    cIssue.showAllMarkers();
    cMessage.clearStatusBarMessage();
  }

  const setBtnState = (state: boolean): CssProperties => {
    var props: CssProperties;
    props = state ? {} : { backgroundColor: btnActive };

    return props;
  }

  const buttonStyling = () => {
    if (_showIssues) {
      const styleObject = { backgroundColor: "#3b86cc", color: "white" };
      return styleObject;
    } else {
      const styleObject = { backgroundColor: "white", color: "black" };
      return styleObject;
    }
  }

  return (
    <div className="issues">
      <div className="issues-search">
        <Input
          type="search"
          id="search"
          placeholder="Search..."
          onChange={onChangeText}
          onKeyDown={onKeyDnSearch}
          disabled={!_showIssues}
          size="small"
        ></Input>
        <Button
          size="small"
          styleType="high-visibility"
          onClick={onButtonSearch}
          disabled={!_showIssues}
        >
          <span className="icon icon-zoom"> </span>
        </Button>
      </div>

      <div className="issues-main">
        <span style={{ fontSize: "medium" }}>
          <span className="icon icon-issue-resolution"> </span>Events
        </span>

        <ToggleSwitch
          onChange={(e) => onToggleShowIssues(e.target.checked)}
        ></ToggleSwitch>

        <Button
          onClick={cIssuesUi.onStartPlaceMarkerTool}
          disabled={!_showIssues}
          size="small"
          styleType="high-visibility"
        >
          <span className="icon icon-issue-resolution-add"> </span>
          {"New Event"}
        </Button>
        <span></span>

        <Input
          type="search"
          id="search"
          defaultValue={_selected}
          placeholder={"Enter Event Id"}
          onChange={onChangeSearch}
          onKeyUp={onSearchKeyUp}
          disabled={!_showIssues}
          size="small"
        ></Input>

        <Button
          onClick={onGoToIssue}
          disabled={!_showIssues}
          size="small"
          styleType="high-visibility"
        >
          <span className="icon icon-zoom"> </span>
        </Button>

        <Button
          onClick={onShowTable}
          disabled={!_showIssues}
          size="small"
          styleType="high-visibility"
        >
          <span className={"icon icon-list"}> </span>
          {"List Events"}
        </Button>

        <span />

        {/*<Button buttonType={ButtonType.Blue} onClick={onToggleShowIcon} 
            style={setBtnState(_showIcons)}
            disabled={!_showIssues}>
            <span className={!_showIcons ? "icon icon-issue-resolution" : "icon icon-model"}> </span>
            {!_showIcons ? "Icon Markers" : "Simple Markers"}
    </Button> */}

        <span>
          <span className="icon icon-label"> </span>
          <span style={{ fontSize: "medium" }}>Show Labels</span>
        </span>
        <ToggleSwitch
          disabled={!_showIssues}
          onChange={(e) => onToggleShowLabel()}
        ></ToggleSwitch>

        <span>
          <span className="icon icon-visibility"> </span>
          <span style={{ fontSize: "medium" }}>Overlay Markers</span>
        </span>
        <ToggleSwitch
          disabled={!_showIssues}
          onChange={(e) => onToggleShowIcon()}
        ></ToggleSwitch>
      </div>

      <div className="issues-filters">
        <span style={{ fontSize: "medium" }}>
          <span className="icon icon-filter"> </span>Filters
        </span>
        <span></span>

        <span>Type</span>
        <Select<string>
          value={_filterType}
          options={[
            { value: "All", label: "All" },
            { value: "Comment", label: "Comment" },
            { value: "Defect", label: "Defect" },
            { value: "Follow-up", label: "Follow-up" },
            { value: "Maintenance", label: "Maintenance" },
            { value: "Other", label: "Other" },
          ]}
          onChange={(option) => setFilterType(option)}
          size="small"
          disabled={!_showIssues}
        />

        <span>Priority</span>
        <Select
          value={_filterPriority}
          options={[
            { value: "All", label: "All" },
            { value: "High", label: "High" },
            { value: "Normal", label: "Normal" },
            { value: "Low", label: "Low" },
          ]}
          disabled={!_showIssues}
          onChange={(option) => setFilterPriority(option)}
          size="small"
        />

        <span>Status</span>
        <Select
          value={_filterStatus}
          options={[
            { value: "All", label: "All" },
            { value: "Open", label: "Open" },
            { value: "Active", label: "Active" },
            { value: "On Hold", label: "On Hold" },
            { value: "Closed", label: "Closed" },
          ]}
          disabled={!_showIssues}
          onChange={(option) => setFilterStatus(option)}
          size="small"
        />

        <span>Since</span>
        <Input
          type="date"
          id="since"
          defaultValue={_filterDate}
          onChange={onChangeDate}
          disabled={!_showIssues}
          size="small"
        />

        <span>Assigned</span>
        <Input
          type="text"
          id="assigned"
          defaultValue={_filterAssigned}
          onChange={onChangeAssignedTo}
          disabled={!_showIssues}
          size="small"
        />
      </div>

      <div className="issues-2col">
        <Button
          onClick={onApply}
          disabled={!_showIssues}
          size="small"
          styleType="high-visibility"
        >
          <span className="icon icon-filter"> </span>
          {"Apply"}
        </Button>
        <Button
          size="small"
          styleType="high-visibility"
          onClick={onClear}
          disabled={!_showIssues}
        >
          <span className="icon icon-filter-outlined"> </span>
          {"Clear"}
        </Button>
      </div>
    </div>
  );
}

export class IssuesWidget implements UiItemsProvider {
  public readonly id = "IssueWidgetUi";

  public provideWidgets(
    stageId: string,
    stageUsage: string,
    location: StagePanelLocation,
    section?: StagePanelSection
  ): ReadonlyArray<AbstractWidgetProps> {
    const widgets: AbstractWidgetProps[] = [];
    if (
      location === StagePanelLocation.Right &&
      section === StagePanelSection.Start
    ) {
      const viewerWidget: AbstractWidgetProps = {
        id: "AppIssueWidget",
        label: "Events",
        defaultState: WidgetState.Floating,
        getWidgetContent() {
          return (
            <div>
              <IssuesUiElements />
            </div>
          );
        },
      }

      widgets.push(viewerWidget);

      //!!OA get the navigation widget so both widgets can be in same panel
      //      const widgetClass = new NavigationWidget();
      //      const navWidget = widgetClass.provideWidgets("","",StagePanelLocation.Right);
      //      widgets.push(navWidget[0]);
    }
    return widgets;
  }
}
